import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardBody, Typography } from '@material-tailwind/react';
import { FaArrowLeft, FaSyncAlt } from 'react-icons/fa';
const { rootUrl } = require('../../../config/config');

const formatMoney = (value) => Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-KE');
};

const reasonLabels = {
    CATALOG_EDIT: 'Catalog Edit',
    PURCHASE_RECEIPT: 'Purchase Receipt'
};

const PartPriceHistory = () => {
    const { part_id } = useParams();
    const navigate = useNavigate();
    const [part, setPart] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchHistory = useCallback(async () => {
        if (!part_id) return;
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            const response = await axios.get(`${rootUrl}/car_parts/${part_id}/price-history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPart(response.data?.data?.part || null);
            setHistory(Array.isArray(response.data?.data?.history) ? response.data.data.history : []);
        } catch (err) {
            console.error('Failed to fetch part price history:', err);
            setError(err?.response?.data?.message || 'Failed to fetch part price history.');
        } finally {
            setLoading(false);
        }
    }, [part_id]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return (
        <div className="w-full max-w-7xl mx-auto mt-6 px-2 sm:px-4">
            <Card className="border-t">
                <CardBody className="p-3 sm:p-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
                        <div>
                            <Typography variant="h5" className="text-gray-900">
                                Part Price History
                            </Typography>
                            <Typography className="text-sm text-gray-600">
                                {part?.part_name || part_id} {part?.part_number ? `(${part.part_number})` : ''}
                            </Typography>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outlined"
                                color="gray"
                                size="sm"
                                onClick={() => navigate('/car_parts')}
                                className="shadow-none rounded-none flex items-center gap-2"
                            >
                                <FaArrowLeft /> Back to Parts
                            </Button>
                            <Button
                                variant="outlined"
                                color="blue"
                                size="sm"
                                onClick={fetchHistory}
                                disabled={loading}
                                className="shadow-none rounded-none flex items-center gap-2"
                            >
                                <FaSyncAlt /> Refresh
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700">{error}</div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
                        <div className="border p-3">
                            <Typography className="text-xs text-gray-500">Current Cost Ex VAT</Typography>
                            <Typography className="text-base font-semibold">{formatMoney(part?.cost_excl)}</Typography>
                        </div>
                        <div className="border p-3">
                            <Typography className="text-xs text-gray-500">Current Cost Inc VAT</Typography>
                            <Typography className="text-base font-semibold">{formatMoney(part?.cost_incl)}</Typography>
                        </div>
                        <div className="border p-3">
                            <Typography className="text-xs text-gray-500">Current Price Ex VAT</Typography>
                            <Typography className="text-base font-semibold">{formatMoney(part?.price_excl)}</Typography>
                        </div>
                        <div className="border p-3">
                            <Typography className="text-xs text-gray-500">Current Price Inc VAT</Typography>
                            <Typography className="text-base font-semibold">{formatMoney(part?.price_incl)}</Typography>
                        </div>
                    </div>

                    {loading ? (
                        <Typography>Loading price history...</Typography>
                    ) : history.length === 0 ? (
                        <div className="border border-dashed border-gray-300 p-6 text-center">
                            <Typography className="text-gray-600">No price changes have been recorded for this part.</Typography>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full table-auto border-collapse min-w-[1100px]">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border px-3 py-2 text-left text-xs">Changed At</th>
                                        <th className="border px-3 py-2 text-left text-xs">Reason</th>
                                        <th className="border px-3 py-2 text-right text-xs">Prev Cost Ex</th>
                                        <th className="border px-3 py-2 text-right text-xs">New Cost Ex</th>
                                        <th className="border px-3 py-2 text-right text-xs">Prev Cost Inc</th>
                                        <th className="border px-3 py-2 text-right text-xs">New Cost Inc</th>
                                        <th className="border px-3 py-2 text-right text-xs">Prev Price Ex</th>
                                        <th className="border px-3 py-2 text-right text-xs">New Price Ex</th>
                                        <th className="border px-3 py-2 text-right text-xs">Prev Price Inc</th>
                                        <th className="border px-3 py-2 text-right text-xs">New Price Inc</th>
                                        <th className="border px-3 py-2 text-left text-xs">Reference</th>
                                        <th className="border px-3 py-2 text-left text-xs">Changed By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map(row => (
                                        <tr key={row.id} className="hover:bg-gray-50">
                                            <td className="border px-3 py-2 text-xs">{formatDateTime(row.changed_at)}</td>
                                            <td className="border px-3 py-2 text-xs">{reasonLabels[row.change_reason] || row.change_reason}</td>
                                            <td className="border px-3 py-2 text-xs text-right">{formatMoney(row.previous_cost_excl)}</td>
                                            <td className="border px-3 py-2 text-xs text-right">{formatMoney(row.new_cost_excl)}</td>
                                            <td className="border px-3 py-2 text-xs text-right">{formatMoney(row.previous_cost_incl)}</td>
                                            <td className="border px-3 py-2 text-xs text-right">{formatMoney(row.new_cost_incl)}</td>
                                            <td className="border px-3 py-2 text-xs text-right">{formatMoney(row.previous_price_excl)}</td>
                                            <td className="border px-3 py-2 text-xs text-right">{formatMoney(row.new_price_excl)}</td>
                                            <td className="border px-3 py-2 text-xs text-right">{formatMoney(row.previous_price_incl)}</td>
                                            <td className="border px-3 py-2 text-xs text-right">{formatMoney(row.new_price_incl)}</td>
                                            <td className="border px-3 py-2 text-xs">
                                                {[row.reference_type, row.reference_id].filter(Boolean).join(': ') || '-'}
                                            </td>
                                            <td className="border px-3 py-2 text-xs">{row.changed_by || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default PartPriceHistory;
